import type { PackReport, UploadLanguagePackArgs } from "@curriculum-studio/core";
import {
  LanguagePackBlobPath,
  LanguageReadiness,
  MAX_PACK_FILE_BYTES,
  packRelativeImagePath,
  packRelativePath,
  UploadLanguagePackResult,
} from "@curriculum-studio/core";
import { HttpClient, HttpClientRequest, HttpClientResponse } from "@effect/platform";
import { Data, Effect, Schema } from "effect";
import { runtime } from "./runtime.ts";

// ADR-0111: the client for `POST /api/language-packs/upload` and
// `GET /api/language-packs/languages`. Deliberately a separate module from `language-pack-actions.ts`
// (the edit surface) — these two endpoints are the CREATE surface, gated on studio-admin rather than
// on being signed in, and keeping them apart keeps each file's failure vocabulary honest.

export class UploadRequestFailed extends Data.TaggedError("UploadRequestFailed")<{
  readonly status: number;
  readonly message: string;
}> {}

const LanguageReadinessList = Schema.Array(LanguageReadiness);

/** A file as the picker hands it over, before anything has been read off disk. */
export interface PickedFile {
  readonly path: string;
  readonly file: File;
}

/**
 * Keep only the files that are pack documents, and read their text.
 *
 * The same `packRelativePath` the server applies, so the preview the human approves is computed over
 * the same file set the server will act on. The rejected files are NOT dropped here — they are sent
 * along so the server's report can name them, rather than the form silently deciding what counts.
 */
export const readPackFiles = async (
  picked: readonly PickedFile[],
): Promise<{
  readonly files: readonly { readonly path: string; readonly content: string }[];
  // ADR-0118: persona images travel as path + size only; their bytes are PUT after the write.
  readonly images: readonly { readonly path: string; readonly size: number }[];
  readonly skipped: readonly string[];
  readonly oversized: readonly string[];
}> => {
  const files: { path: string; content: string }[] = [];
  const images: { path: string; size: number }[] = [];
  const skipped: string[] = [];
  const oversized: string[] = [];
  for (const entry of picked) {
    if (packRelativeImagePath(entry.path) !== undefined) {
      images.push({ path: entry.path, size: entry.file.size });
      continue;
    }
    if (packRelativePath(entry.path) === undefined) {
      skipped.push(entry.path);
      continue;
    }
    // Checked before the read: a binary picked up by a loose extension match would otherwise be
    // decoded as text and posted, only to be refused at the decode boundary with a size error that
    // says nothing about which file caused it.
    if (entry.file.size > MAX_PACK_FILE_BYTES) {
      oversized.push(entry.path);
      continue;
    }
    files.push({ path: entry.path, content: await entry.file.text() });
  }
  return { files, images, skipped, oversized };
};

const decodeBlobPath = Schema.decodeUnknownOption(LanguagePackBlobPath);

/**
 * PUT each image the server accepted (`result.images`: the picked path it was sent under, and the
 * pack path it goes to). Sequential — a pack's kit is a few dozen images and a partial failure
 * should name the image that failed. Returns the paths written and the ones that failed.
 */
export const uploadPackImages = async (
  picked: readonly PickedFile[],
  accepted: readonly { readonly from: string; readonly path: string }[],
): Promise<{ readonly written: readonly string[]; readonly failed: readonly string[] }> => {
  const byPath = new Map(picked.map((entry) => [entry.path, entry.file]));
  const written: string[] = [];
  const failed: string[] = [];
  for (const image of accepted) {
    const file = byPath.get(image.from);
    const path = decodeBlobPath(image.path);
    if (file === undefined || path._tag === "None") {
      failed.push(image.path);
      continue;
    }
    // `Uint8Array.from`: see `language-pack-template-download.ts` on the cross-realm trap.
    const bytes = Uint8Array.from(new Uint8Array(await file.arrayBuffer()));
    const result = await runtime.runPromise(
      Effect.gen(function* () {
        const client = yield* HttpClient.HttpClient;
        return yield* client.execute(
          HttpClientRequest.put("/api/language-packs/blob").pipe(
            HttpClientRequest.setUrlParams({ path: path.value }),
            HttpClientRequest.bodyUint8Array(bytes, "application/octet-stream"),
          ),
        );
      }).pipe(
        Effect.scoped,
        Effect.map((response) => response.status < 400),
        Effect.catchAll(() => Effect.succeed(false)),
      ),
    );
    (result ? written : failed).push(image.path);
  }
  return { written, failed };
};

export const uploadLanguagePack = (args: UploadLanguagePackArgs) =>
  Effect.gen(function* () {
    const client = yield* HttpClient.HttpClient;
    const request = yield* HttpClientRequest.post("/api/language-packs/upload").pipe(
      HttpClientRequest.bodyJson(args),
    );
    const response = yield* client.execute(request);
    if (response.status >= 400) {
      return yield* Effect.fail(
        new UploadRequestFailed({
          status: response.status,
          message:
            response.status === 403
              ? "Only a studio admin can upload a language pack."
              : response.status === 409
                ? "A document in this upload changed while you were reviewing it. Re-run the check and confirm again."
                : `The upload failed (HTTP ${response.status}).`,
        }),
      );
    }
    return yield* HttpClientResponse.schemaBodyJson(UploadLanguagePackResult)(response);
  }).pipe(Effect.scoped);

export const listLanguageReadiness = Effect.gen(function* () {
  const client = yield* HttpClient.HttpClient;
  const response = yield* client.execute(HttpClientRequest.get("/api/language-packs/languages"));
  if (response.status >= 400) {
    return yield* Effect.fail(
      new UploadRequestFailed({
        status: response.status,
        message: `Could not load the language list (HTTP ${response.status}).`,
      }),
    );
  }
  return yield* HttpClientResponse.schemaBodyJson(LanguageReadinessList)(response);
}).pipe(Effect.scoped);

// The `Promise`-returning surface the components use, collapsing every failure into a decided result
// rather than a rejection — the `SaveResult` pattern from `language-pack-actions.ts`.
export type UploadResult =
  | {
      readonly _tag: "ok";
      readonly report: PackReport;
      readonly written: readonly string[];
      readonly accepted: boolean;
      readonly images: readonly { readonly from: string; readonly path: string }[];
    }
  | { readonly _tag: "failed"; readonly message: string };

export const languagePackUploadActions = {
  check: (args: UploadLanguagePackArgs): Promise<UploadResult> =>
    runtime.runPromise(
      uploadLanguagePack(args).pipe(
        Effect.map(
          (result): UploadResult => ({
            _tag: "ok",
            report: result.report,
            written: result.written,
            accepted: result.accepted,
            images: result.images,
          }),
        ),
        Effect.catchTag("UploadRequestFailed", (error) =>
          Effect.succeed<UploadResult>({ _tag: "failed", message: error.message }),
        ),
        Effect.catchAll(() =>
          Effect.succeed<UploadResult>({
            _tag: "failed",
            message: "The upload failed unexpectedly.",
          }),
        ),
      ),
    ),
  languages: (): Promise<readonly LanguageReadiness[]> =>
    runtime.runPromise(listLanguageReadiness.pipe(Effect.catchAll(() => Effect.succeed([])))),
};
